import { useState, useEffect, useRef } from "react";
import type { TaskExtended, Task, TaskCreateInput } from "@/types";
import { TaskStatus, TaskType } from "@/types";
import { useSubtasks } from "@/hooks/useTasks";
import { useSubtaskSubscription } from "@/hooks/useTaskSubscription";
import { hasCapability, ProjectCapability } from "@/lib/projectCapabilities";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useSyncInspectionTopics } from "@/hooks/useInspectionSync";
import {
  CREATE_TASK,
  DELETE_TASK,
  CREATE_TASK_COMMENT,
  GET_TASK_COMMENTS,
  ADD_SUBTASK_TO_TASK_SERIES,
  UPDATE_SUBTASK_IN_TASK_SERIES,
  DELETE_SUBTASK_FROM_TASK_SERIES,
  DELETE_TASK_SERIES,
} from "@/graphql/mutations";
import { SeriesScopeDialog } from "@/components/tasks/SeriesScopeDialog";
import { TaskMetadataEditDialog } from "@/components/tasks/TaskMetadataEditDialog";
import { formatResponsible, effectiveInitials } from "@/lib/responsibleUser";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DialogTabs, DialogTabsList, DialogTabsTrigger, DialogTabsContent } from "@/components/ui/dialog-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateFi, formatRelativeTimeFi, getDueDateInfo } from "@/lib/format";
import {
  SuccessIcon,
  DateIcon,
  UserIcon,
  TimeIcon,
  SpaceIcon,
  PlotIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  CloseIcon,
  CheckmarkIcon,
  SpinnerIcon,
  ProjectIcon,
  WorkSectionIcon,
  ProcurementPackageIcon,
  CongridIcon,
  DocumentIcon,
  RecurrenceIcon,
} from "@/lib/icons";
import { cn } from "@/lib/utils";
import { TASK_STATUS_ICONS, TASK_STATUS_COLORS } from "@/lib/taskStatusMeta";
import { CompletionToggle } from "@/components/tasks/TaskSummaryCard";
import { getNextStatus, StatusCheckmark } from "@/components/tasks/StatusCheckmark";
import type { TaskCompletionToggle } from "@/hooks/useTaskCompletionToggle";
import {
  ConfettiBurst,
  SparkBurst,
  CONFETTI_MS,
  SPARK_MS,
  useCelebration,
} from "@/components/tasks/CompletionEffects";

const UPDATE_TASK = gql`
  mutation UpdateTask($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      success
      message
      task {
        id
        title
        description
        status
        taskType
        parentId
        projectId
        sectionId
        locationId
        dueDate
        startedAt
        completedAt
        responsibleUserId
        createdByUserId
        responsibleUser {
          id
          firstName
          lastName
          azureObjectId
        }
        version
        createdAt
        updatedAt
        procurementPackage {
          code
          name
        }
        section {
          id
          name
          procurementPackage {
            code
            name
          }
        }
        location {
          id
          name
          locationType
          hierarchyPath
        }
      }
    }
  }
`;

const SET_INSPECTION_TOPIC_TASK_STATUS = gql`
  mutation SetInspectionTopicTaskStatus($id: String!, $status: TaskStatus!) {
    setInspectionTopicTaskStatus(id: $id, status: $status) {
      success
      message
      task {
        id
        status
        version
        updatedAt
        completedAt
      }
    }
  }
`;

const GET_TASK_DETAIL = gql`
  query GetTaskDetail($id: String!) {
    task(id: $id) {
      id
      title
      description
      status
      taskType
      parentId
      projectId
      sectionId
      locationId
      plotId
      dueDate
      startedAt
      completedAt
      externalOrder
      responsibleUserId
      createdByUserId
      recurrenceSeriesId
      occurrenceDate
      isRecurring
      isOffSchedule
      recurrenceSeries {
        id
        frequency
        intervalCount
        weekday
        weekOfMonth
        startDate
        endDate
        rootSeriesId
        version
      }
      externalInspectionPlatformInspectionId
      congridInspectionUrl
      viewerCanWrite
      viewerCanAddSubtask
      version
      createdAt
      updatedAt
      responsibleUser {
        id
        firstName
        lastName
        azureObjectId
        substituteUserId
        substituteFirstName
        substituteLastName
      }
      inheritedResponsibleUser {
        id
        firstName
        lastName
        substituteUserId
        substituteFirstName
        substituteLastName
      }
      procurementPackage {
        code
        name
      }
      section {
        id
        name
        code
        procurementPackage {
          code
          name
        }
      }
      location {
        id
        name
        locationType
        hierarchyPath
      }
      project {
        id
        name
        viewerCapabilities
      }
      plot {
        id
        name
      }
      subtaskProgress {
        pending
        inProgress
        completed
        problem
        total
      }
      sitedriveMetadata {
        sitedriveWorkPackage
        sitedriveParentWorkPackage
        sitedriveGrandparentWorkPackage
      }
    }
  }
`;

interface TaskDialogProps {
  task: TaskExtended | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateStatus: (taskId: string, status: TaskStatus) => void;
  currentUserId?: string;
  /**
   * Called when the task's metadata is edited from inside the dialog.
   * Parents that hold the task in useState should use this to refresh
   * their snapshot so the dialog re-renders without needing to be reopened.
   */
  onTaskUpdated?: (task: TaskExtended) => void;
  /**
   * Called whenever subtask data changes (sync complete, status update,
   * create, delete). Parents should refetch their task list so the
   * TaskSummaryCard progress bar reflects the latest state.
   */
  onSubtasksChanged?: () => void;
}

interface TaskCommentWithWriter {
  id: string;
  content: string;
  createdAt: string;
  writer?: { id: string; firstName?: string | null; lastName?: string | null } | null;
}

/**
 * Would this set of subtasks derive a COMPLETED parent? Mirrors
 * `TaskService.derive_parent_status` on the backend, which owns the real
 * value — this copy only exists so the dialog can tell the moment the parent
 * tips over to done and celebrate it without waiting for a round trip.
 */
function subtasksImplyCompleted(list: Task[]): boolean {
  if (list.length === 0) return false;
  if (
    list.some(
      (s) =>
        s.status === TaskStatus.REJECTED ||
        s.status === TaskStatus.CANNOT_MEASURE,
    )
  ) {
    return false;
  }
  return list.every(
    (s) =>
      s.status === TaskStatus.COMPLETED ||
      s.status === TaskStatus.NOT_APPLICABLE,
  );
}

/**
 * Opens a URL in a new tab by clicking a synthetic anchor instead of calling
 * window.open. iOS Safari creates the new tab immediately on window.open and
 * only then resolves the URL, so a universal link that hands off to a native
 * app leaves an empty about:blank tab behind — the user comes back from the
 * Congrid app to a white page. An anchor navigation is handed off during the
 * navigation policy check, before any tab exists.
 */
function openInNewTab(url: string) {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function SubtaskItem({
  subtask,
  onUpdateStatus,
  canManage,
  canEditStatus,
  onEdit,
  onDelete,
  parentCongridUrl,
  indexLabel,
}: {
  subtask: Task;
  onUpdateStatus: (
    taskId: string,
    status: TaskStatus,
    version: number,
  ) => Promise<number | undefined>;
  canManage: boolean;
  canEditStatus: boolean;
  onEdit: (subtask: Task) => void;
  onDelete: (subtask: Task) => void;
  parentCongridUrl?: string | null;
  indexLabel?: string;
}) {
  const [justCompleted, setJustCompleted] = useState(false);
  const [sparkRun, fireSpark] = useCelebration(SPARK_MS);
  // Optimistic local status: flip the icon instantly on click, then reconcile
  // when fresh data arrives (or snap back if the mutation fails and the prop
  // stays on the old status).
  const [displayStatus, setDisplayStatus] = useState(subtask.status);

  // Authoritative row version, kept in a ref so chained clicks can send the
  // version returned by the *previous* mutation instead of the (stale) prop.
  const versionRef = useRef(subtask.version);
  // FIFO of statuses the user has clicked but that haven't been persisted yet.
  // Drained serially so each mutation uses the freshest version — this is what
  // stops a rapid double-click from firing with a stale version, getting
  // rejected by optimistic locking, and reverting the icon.
  const queueRef = useRef<TaskStatus[]>([]);
  const drainingRef = useRef(false);

  // Reconcile with server truth only while idle. Mid-drain the prop is older
  // than our optimistic clicks, so ignore it until the queue empties.
  useEffect(() => {
    if (!drainingRef.current && queueRef.current.length === 0) {
      setDisplayStatus(subtask.status);
      versionRef.current = subtask.version;
    }
  }, [subtask.status, subtask.version]);

  const wasCompleted = displayStatus === TaskStatus.COMPLETED;
  const isCongridTopic =
    subtask.taskType === TaskType.INSPECTION_SUBTASK;

  const drainQueue = async () => {
    if (drainingRef.current) return;
    drainingRef.current = true;
    try {
      while (queueRef.current.length > 0) {
        const target = queueRef.current[0];
        const newVersion = await onUpdateStatus(
          subtask.id,
          target,
          versionRef.current,
        );
        queueRef.current.shift();
        if (typeof newVersion === "number") {
          versionRef.current = newVersion;
        } else {
          // Mutation failed (version conflict, validation, network). Abandon
          // the rest of the queue; the parent refetches to resync the prop,
          // and the effect above snaps the icon back to server truth.
          queueRef.current = [];
          break;
        }
      }
    } finally {
      drainingRef.current = false;
    }
  };

  const handleStatusClick = () => {
    // No project write access (mirrors backend check_project_access):
    // the symbol is rendered disabled, but guard the handler too.
    if (!canEditStatus) {
      return;
    }
    // Congrid topic status cannot be legally changed via our API user —
    // the "editor" field is server-attributed to the API caller and
    // accepting on behalf of someone else is not allowed. Send the user
    // to Congrid instead.
    if (isCongridTopic) {
      if (parentCongridUrl) {
        openInNewTab(parentCongridUrl);
      }
      return;
    }
    // Build the next status from the last queued intent (or the current
    // display), so several fast clicks step through the cycle correctly.
    const base =
      queueRef.current.length > 0
        ? queueRef.current[queueRef.current.length - 1]
        : displayStatus;
    const nextStatus = getNextStatus(base);
    if (nextStatus === TaskStatus.COMPLETED && base !== TaskStatus.COMPLETED) {
      setJustCompleted(true);
      setTimeout(() => setJustCompleted(false), 700);
      fireSpark();
    }
    setDisplayStatus(nextStatus);
    queueRef.current.push(nextStatus);
    void drainQueue();
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2.5 px-3 rounded-md border border-border/60 bg-card shadow-sm hover:shadow hover:border-border transition-all group",
        justCompleted && "motion-safe:animate-[flashGreen_0.7s_ease-out]",
      )}
    >
      <div
        className="relative mt-0.5"
        title={
          !canEditStatus
            ? "Sinulla ei ole oikeuksia muuttaa tämän tehtävän tilaa"
            : isCongridTopic
              ? "Tilan muuttaminen tapahtuu Congridissa"
              : undefined
        }
      >
        <SparkBurst runId={sparkRun} className="z-10" />
        <StatusCheckmark
          status={displayStatus}
          onClick={handleStatusClick}
          justCompleted={justCompleted}
          startedAt={subtask.startedAt}
          completedAt={subtask.completedAt}
          disabled={!canEditStatus}
        />
      </div>
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            "text-sm block transition-all duration-300",
            wasCompleted && "line-through text-muted-foreground",
          )}
        >
          {indexLabel && (
            <span className="mr-1.5 tabular-nums text-muted-foreground font-medium">
              {indexLabel}
            </span>
          )}
          {subtask.title}
        </span>
        {subtask.description && (
          <span className="text-xs text-muted-foreground block mt-1">
            {subtask.description}
          </span>
        )}
      </div>
      {/* A tarkastuskohta (Congrid topic) is edited and removed exclusively
          through the Tarkastuskohdat section in Muokkaa tehtävän tietoja now,
          which rewrites the whole list at once — the per-row controls below
          would edit or delete this subtask locally without touching Congrid,
          so they're left out for this row type. */}
      {canManage && !isCongridTopic && (
        <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => onEdit(subtask)}
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Muokkaa alitehtävää"
          >
            <EditIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(subtask)}
            className="h-7 w-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Poista alitehtävä"
          >
            <DeleteIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

function EditableSubtaskItem({
  subtask,
  onSave,
  onCancel,
  saving,
}: {
  subtask: Task;
  onSave: (title: string, description: string) => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  const [title, setTitle] = useState(subtask.title);
  const [description, setDescription] = useState(subtask.description || "");

  const handleSave = () => {
    if (!title.trim() || saving) return;
    onSave(title.trim(), description.trim());
  };

  return (
    <div className="py-2 px-3 rounded-md border border-primary/30 bg-accent/30 space-y-2">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Alitehtävän nimi"
        className="text-base md:text-sm h-8"
        autoFocus
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Kuvaus (valinnainen)"
        className="text-base md:text-sm min-h-[50px] resize-none"
        rows={2}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving} className="h-7 px-2">
          <CloseIcon className="h-3.5 w-3.5 mr-1" />
          Peruuta
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!title.trim() || saving} className="h-7 px-2">
          {saving ? <SpinnerIcon className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckmarkIcon className="h-3.5 w-3.5 mr-1" />}
          {saving ? "Tallennetaan..." : "Tallenna"}
        </Button>
      </div>
    </div>
  );
}

function NewSubtaskForm({
  onSave,
  onCancel,
  saving,
}: {
  onSave: (title: string, description: string) => void;
  onCancel: () => void;
  saving?: boolean;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const handleSave = () => {
    if (!title.trim() || saving) return;
    onSave(title.trim(), description.trim());
  };

  return (
    <div className="py-2 px-3 rounded-md border border-dashed border-primary/30 bg-accent/20 space-y-2">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Uuden alitehtävän nimi"
        className="text-base md:text-sm h-8"
        autoFocus
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Kuvaus (valinnainen)"
        className="text-base md:text-sm min-h-[50px] resize-none"
        rows={2}
        disabled={saving}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !saving) onCancel();
        }}
      />
      <div className="flex items-center gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving} className="h-7 px-2">
          <CloseIcon className="h-3.5 w-3.5 mr-1" />
          Peruuta
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!title.trim() || saving} className="h-7 px-2">
          {saving ? <SpinnerIcon className="h-3.5 w-3.5 mr-1 animate-spin" /> : <AddIcon className="h-3.5 w-3.5 mr-1" />}
          {saving ? "Lisätään..." : "Lisää"}
        </Button>
      </div>
    </div>
  );
}

export function TaskDialog({
  task: taskProp,
  open,
  onOpenChange,
  onUpdateStatus: _onUpdateStatus,
  currentUserId,
  onTaskUpdated,
  onSubtasksChanged,
}: TaskDialogProps) {
  const [newComment, setNewComment] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const {
    data: commentsData,
    refetch: refetchComments,
  } = useQuery<{ taskComments: { items: TaskCommentWithWriter[] } }>(
    GET_TASK_COMMENTS,
    {
      variables: { taskId: taskProp?.id ?? "" },
      skip: !taskProp?.id,
      fetchPolicy: "cache-and-network",
    },
  );
  const comments: TaskCommentWithWriter[] = commentsData?.taskComments.items ?? [];
  const [createCommentMutation, { loading: creatingComment }] = useMutation(CREATE_TASK_COMMENT);
  const { subtasks, loading: subtasksLoading, refetch: refetchSubtasks } = useSubtasks(taskProp?.id ?? null);

  // Subscribe to real-time subtask changes (from this user or others).
  useSubtaskSubscription(taskProp?.id ?? null, refetchSubtasks);

  const [updateTaskMutation] = useMutation(UPDATE_TASK);
  const [setTopicStatusMutation] = useMutation(SET_INSPECTION_TOPIC_TASK_STATUS);
  const { syncInspection: syncInspectionTopics } = useSyncInspectionTopics();
  // Parent status is now auto-derived server-side inside the updateTask
  // mutation, so updateParentStatusMutation is no longer needed.
  const [createTaskMutation] = useMutation(CREATE_TASK);
  const [deleteTaskMutation] = useMutation(DELETE_TASK);
  const [deleteTaskSeriesMutation] = useMutation(DELETE_TASK_SERIES);
  const [addSubtaskToSeriesMutation] = useMutation(ADD_SUBTASK_TO_TASK_SERIES);
  const [updateSubtaskInSeriesMutation] = useMutation(UPDATE_SUBTASK_IN_TASK_SERIES);
  const [deleteSubtaskFromSeriesMutation] = useMutation(DELETE_SUBTASK_FROM_TASK_SERIES);

  // The Kanban board refetch is heavy (3 full column queries). Rather than
  // firing it on every subtask change, flag the board dirty here and refetch
  // once when the dialog closes (see the close-transition effect below).
  const boardDirtyRef = useRef(false);

  // Lazy-load the full task when the dialog opens. Callers (e.g. Etusivu's
  // recent-activity list) may pass a minimal task snapshot that lacks fields
  // like location.hierarchyPath or congridInspectionUrl. Apollo serves cached
  // data instantly when available, so there is no visible delay for callers
  // that already pass the full task (GET_TASKS result is already in cache).
  const {
    data: detailData,
    loading: detailLoading,
    refetch: refetchTaskDetail,
  } = useQuery<{
    task: TaskExtended | null;
  }>(GET_TASK_DETAIL, {
    variables: { id: taskProp?.id ?? "" },
    skip: !open || !taskProp?.id,
    fetchPolicy: "cache-and-network",
  });
  const task = detailData?.task ?? taskProp;

  // Edit-metadata dialog visibility — also hosts the tarkastuskohdat editor
  // for an INSPECTION_MAIN_TASK, see TaskMetadataEditDialog.
  const [editMetadataOpen, setEditMetadataOpen] = useState(false);

  // Subtask management state
  const [showNewSubtask, setShowNewSubtask] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [subtaskToDelete, setSubtaskToDelete] = useState<Task | null>(null);
  const [subtaskError, setSubtaskError] = useState<string | null>(null);
  const [subtaskSaving, setSubtaskSaving] = useState(false);
  const [confettiRun, fireConfetti] = useCelebration(CONFETTI_MS);

  // Which write is waiting on the user's answer to "koko sarja vai vain tämä?".
  // Null means no prompt is open; the prompt is only ever raised for a task
  // that belongs to a sarja.
  const [pendingScope, setPendingScope] = useState<
    | { kind: "deleteTask" }
    | { kind: "addSubtask"; title: string; description: string }
    | { kind: "editSubtask"; subtaskId: string; title: string; description: string }
    | { kind: "deleteSubtask" }
    | null
  >(null);

  // Top-level task deletion state (confirm dialog + inline error).
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Authorization hints come from the server, resolved by the same predicates
  // the mutation gates enforce (backend/app/core/access_policy.py). They are
  // deliberately NOT recomputed here: the previous client-side approximation
  // treated all five ProjectRoleTypes as equivalent, so it showed Muokkaa /
  // Poista to Työnjohtaja-tier roles on tasks they do not own — which the
  // backend then rejected with a 403.
  //
  // Undefined (field not selected) is treated as "no" so a query that forgets
  // the field hides the control rather than offering a doomed action.
  const canWriteTask = task?.viewerCanWrite ?? false;
  // Inspection main tasks are excluded regardless of what the server answers:
  // their children are Congrid topics, mirrored by the inspection sync, and
  // adding one here is not a feature this app offers.
  const canAddSubtask =
    (task?.viewerCanAddSubtask ?? false) &&
    task?.taskType !== TaskType.INSPECTION_MAIN_TASK;
  // Commenting is a project-level capability every role holds, so it is gated
  // separately from task write access.
  const canComment = hasCapability(task?.project, ProjectCapability.COMMENT);

  // On open, refresh Congrid inspection topics so the list reflects the
  // latest state before the user starts clicking around. Fire-and-forget:
  // refetch subtasks whether the sync hit the wire or not.
  useEffect(() => {
    if (!open || !task) return;
    console.debug("[TaskDialog] sync-on-open check", {
      taskId: task.id,
      taskType: task.taskType,
      inspectionId: task.externalInspectionPlatformInspectionId,
    });
    if (
      task.taskType !== TaskType.INSPECTION_MAIN_TASK ||
      !task.externalInspectionPlatformInspectionId
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        console.debug(
          "[TaskDialog] syncing topics for inspection",
          task.externalInspectionPlatformInspectionId,
        );
        const res = await syncInspectionTopics(
          task.externalInspectionPlatformInspectionId!,
        );
        console.debug("[TaskDialog] sync result:", res);
      } catch (err) {
        console.warn("Inspection topic sync-on-open failed:", err);
      }
      if (!cancelled) {
        try {
          await refetchSubtasks();
        } catch (err) {
          console.warn("Subtask refetch after sync failed:", err);
        }
        onSubtasksChanged?.();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id, task?.externalInspectionPlatformInspectionId]);

  // On close: refresh the Kanban board once, instead of on every subtask
  // change. A subtask edit only flips `boardDirtyRef`; the heavy board
  // refetch (onSubtasksChanged) fires here, on the close transition, and only
  // if something actually changed. Inspection tasks additionally re-sync
  // Congrid topics so changes made on the Congrid website while the dialog
  // was open get picked up, so they always refresh on close.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    // Detect close transition: was open, now closed.
    if (!wasOpen || open) return;

    const isInspection =
      !!task &&
      task.taskType === TaskType.INSPECTION_MAIN_TASK &&
      !!task.externalInspectionPlatformInspectionId;

    if (isInspection) {
      (async () => {
        try {
          await syncInspectionTopics(task!.externalInspectionPlatformInspectionId!);
        } catch (err) {
          console.warn("Inspection topic sync-on-close failed:", err);
        }
        onSubtasksChanged?.();
        boardDirtyRef.current = false;
      })();
    } else if (boardDirtyRef.current) {
      onSubtasksChanged?.();
      boardDirtyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset editing states when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setEditMetadataOpen(false);
      setShowNewSubtask(false);
      setEditingSubtaskId(null);
      setSubtaskToDelete(null);
      setSubtaskError(null);
      setConfirmDeleteOpen(false);
      setDeleteError(null);
    }
  }, [open]);

  if (!task) return null;

  // Callers such as the laatumatriisi open the dialog with an id-only snapshot,
  // so every field the header paints is still undefined on the first render.
  // Show the card's shape until GET_TASK_DETAIL lands rather than a header with
  // nothing in it.
  if (!task.title) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-screen h-dvh pb-0 max-w-full max-h-dvh rounded-none overflow-hidden flex flex-col border-0 shadow-xl [&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-white [&>button]:opacity-80 [&>button]:rounded-md [&>button:hover]:opacity-100 [&>button:hover]:bg-white/15 md:w-full md:h-[min(720px,90dvh)] md:max-w-3xl md:max-h-full md:rounded-lg"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">Ladataan tehtävää</DialogTitle>
          {detailLoading ? (
            <>
              <div className="bg-primary px-6 py-5 md:rounded-t-lg">
                <Skeleton className="h-7 w-2/3 bg-white/25" />
                <Skeleton className="mt-3 h-4 w-1/2 bg-white/20" />
              </div>
              <div className="flex-1 space-y-3 px-6 py-5">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              Tehtävää ei löytynyt.
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !task) return;
    if (!currentUserId) {
      setCommentError("Käyttäjää ei tunnistettu. Kirjaudu sisään uudelleen.");
      return;
    }
    setCommentError(null);
    try {
      const result = await createCommentMutation({
        variables: {
          input: {
            taskId: task.id,
            writerId: currentUserId,
            content: newComment.trim(),
          },
        },
      });
      if (!result.data?.createTaskComment?.success) {
        setCommentError(result.data?.createTaskComment?.message || "Kommentin lisäys epäonnistui");
        return;
      }
      setNewComment("");
      await refetchComments();
    } catch (error) {
      console.error("Failed to create comment:", error);
      setCommentError("Kommentin lisäys epäonnistui");
    }
  };

  /**
   * Fire the parent-level confetti on the click that tips the last subtask
   * over. Compares the list as it stands against the list with this one
   * status swapped in, so it triggers once on the edge and stays quiet when
   * an already-complete list is clicked around.
   */
  const celebrateIfParentCompleted = (taskId: string, status: TaskStatus) => {
    if (subtasksImplyCompleted(subtasks)) return;
    const projected = subtasks.map((s) =>
      s.id === taskId ? { ...s, status } : s,
    );
    if (subtasksImplyCompleted(projected)) {
      fireConfetti();
    }
  };

  const handleSubtaskStatusUpdate = async (
    taskId: string,
    status: TaskStatus,
    version: number,
  ): Promise<number | undefined> => {

    // As mentioned in SubtaskItem, the status of Congrid inspection topic tasks cannot be changed
    // via our API user. Instead of attempting a mutation that would be rejected by the server,
    // send the user to Congrid to make the change there.

    const subtask = subtasks.find((s) => s.id === taskId);
    const isCongridTopic =
      subtask?.taskType === TaskType.INSPECTION_SUBTASK;

    try {
      if (isCongridTopic) {
        const result = await setTopicStatusMutation({
          variables: { id: taskId, status },
        });
        if (!result.data?.setInspectionTopicTaskStatus?.success) {
          console.error(
            "Congrid topic status sync failed:",
            result.data?.setInspectionTopicTaskStatus?.message,
          );
          return undefined;
        }
        celebrateIfParentCompleted(taskId, status);
        await refetchSubtasks();
        boardDirtyRef.current = true;
        return undefined;
      }

      const result = await updateTaskMutation({
        variables: {
          input: {
            id: taskId,
            version,
            status,
          },
        },
      });
      if (result.errors) {
        console.error("GraphQL errors:", result.errors);
      }
      if (result.data?.updateTask?.success) {
        boardDirtyRef.current = true;
        celebrateIfParentCompleted(taskId, status);
        // The mutation returns the updated task, which Apollo writes into the
        // normalized cache by id — the subtask list reconciles reactively with
        // no extra round trip. Hand the fresh version back so the caller can
        // chain the next queued click without a stale-version rejection.
        return result.data.updateTask.task?.version as number | undefined;
      }
      // Failure (version conflict, validation): resync from the server so the
      // icon returns to the true status.
      console.error("Mutation failed:", result.data?.updateTask?.message);
      await refetchSubtasks();
      return undefined;
    } catch (error) {
      console.error("Failed to update subtask status:", error);
      await refetchSubtasks();
      return undefined;
    }
  };

  /**
   * Status toggle for this task when it has no subtasks — without one its
   * status would be stuck, since a parent's status is otherwise derived from
   * its children. The board is only flagged dirty (not refetched) so the
   * columns behind the dialog reload once on close, same as every other
   * write in here.
   */
  const handleToggleCompleted: TaskCompletionToggle = async (
    taskId,
    status,
    version,
  ) => {
    try {
      const result = await updateTaskMutation({
        variables: { input: { id: taskId, version, status } },
      });
      if (!result.data?.updateTask?.success) {
        setSubtaskError(
          result.data?.updateTask?.message || "Tehtävän tilan päivitys epäonnistui.",
        );
        return undefined;
      }
      boardDirtyRef.current = true;
      return result.data.updateTask.task?.version as number | undefined;
    } catch (error) {
      console.error("Failed to toggle task completion:", error);
      setSubtaskError("Tehtävän tilan päivitys epäonnistui.");
      return undefined;
    }
  };

  const handleCreateSubtask = async (
    title: string,
    description: string,
    scope?: "single" | "series",
  ) => {
    if (!task) return;
    if (task.isRecurring && !scope) {
      setPendingScope({ kind: "addSubtask", title, description });
      return;
    }
    setSubtaskError(null);
    setSubtaskSaving(true);
    try {
      if (scope === "series") {
        const seriesResult = await addSubtaskToSeriesMutation({
          variables: {
            input: {
              parentTaskId: task.id,
              title,
              description: description || null,
            },
          },
        });
        if (seriesResult.data?.addSubtaskToTaskSeries?.success) {
          await refetchSubtasks();
          boardDirtyRef.current = true;
          setShowNewSubtask(false);
        } else {
          setSubtaskError(
            seriesResult.data?.addSubtaskToTaskSeries?.message ||
              "Alitehtävän luonti epäonnistui.",
          );
        }
        return;
      }

      const input: TaskCreateInput = {
        title,
        description: description || undefined,
        status: TaskStatus.PENDING,
        taskType:
          task.taskType === TaskType.TEMPLATE_MAIN_TASK
            ? TaskType.TEMPLATE_SUBTASK
            : TaskType.INTERNAL_SUBTASK,
        parentId: task.id,
        createdByUserId: currentUserId || undefined,
      };
      const result = await createTaskMutation({ variables: { input } });
      if (result.data?.createTask?.success) {
        await refetchSubtasks();
        boardDirtyRef.current = true;
        setShowNewSubtask(false);
      } else {
        const msg = result.data?.createTask?.message || "Alitehtävän luonti epäonnistui.";
        setSubtaskError(msg);
        console.error("Failed to create subtask:", msg);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Alitehtävän luonti epäonnistui. Yritä uudelleen.";
      setSubtaskError(msg);
      console.error("Failed to create subtask:", error);
    } finally {
      setSubtaskSaving(false);
    }
  };

  const handleEditSubtask = async (
    subtaskId: string,
    title: string,
    description: string,
    scope?: "single" | "series",
  ) => {
    const subtask = subtasks.find((s) => s.id === subtaskId);
    if (!subtask) return;
    if (subtask.isRecurring && !scope) {
      setPendingScope({ kind: "editSubtask", subtaskId, title, description });
      return;
    }
    setSubtaskError(null);
    setSubtaskSaving(true);
    try {
      if (scope === "series") {
        const seriesResult = await updateSubtaskInSeriesMutation({
          variables: {
            input: { id: subtaskId, title, description: description || null },
          },
        });
        if (seriesResult.data?.updateSubtaskInTaskSeries?.success) {
          await refetchSubtasks();
          setEditingSubtaskId(null);
        } else {
          setSubtaskError(
            seriesResult.data?.updateSubtaskInTaskSeries?.message ||
              "Alitehtävän muokkaus epäonnistui.",
          );
        }
        return;
      }

      const result = await updateTaskMutation({
        variables: {
          input: {
            id: subtaskId,
            version: subtask.version,
            title,
            description: description || null,
          },
        },
      });
      if (result.data?.updateTask?.success) {
        await refetchSubtasks();
        setEditingSubtaskId(null);
      } else {
        const msg = result.data?.updateTask?.message || "Alitehtävän muokkaus epäonnistui.";
        setSubtaskError(msg);
        console.error("Failed to update subtask:", msg);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Alitehtävän muokkaus epäonnistui. Yritä uudelleen.";
      setSubtaskError(msg);
      console.error("Failed to update subtask:", error);
    } finally {
      setSubtaskSaving(false);
    }
  };

  const handleDeleteSubtask = async (scope?: "single" | "series") => {
    if (!subtaskToDelete) return;
    if (subtaskToDelete.isRecurring && !scope) {
      setPendingScope({ kind: "deleteSubtask" });
      return;
    }
    setSubtaskError(null);
    try {
      if (scope === "series") {
        const seriesResult = await deleteSubtaskFromSeriesMutation({
          variables: { id: subtaskToDelete.id },
        });
        if (seriesResult.data?.deleteSubtaskFromTaskSeries?.success) {
          await refetchSubtasks();
          boardDirtyRef.current = true;
          setSubtaskToDelete(null);
        } else {
          setSubtaskError(
            seriesResult.data?.deleteSubtaskFromTaskSeries?.message ||
              "Alitehtävän poisto epäonnistui.",
          );
        }
        return;
      }

      const result = await deleteTaskMutation({
        variables: { id: subtaskToDelete.id },
      });
      console.log("[TaskDialog] Delete result:", JSON.stringify(result.data), "errors:", result.errors);
      if (result.data?.deleteTask?.success) {
        await refetchSubtasks();
        boardDirtyRef.current = true;
        setSubtaskToDelete(null);
      } else {
        const msg = result.data?.deleteTask?.message || "Alitehtävän poisto epäonnistui.";
        setSubtaskError(msg);
        console.error("Failed to delete subtask:", msg);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Alitehtävän poisto epäonnistui. Yritä uudelleen.";
      setSubtaskError(msg);
      console.error("Failed to delete subtask:", error);
    }
  };

  const handleDeleteTask = async (scope?: "single" | "series") => {
    if (!task) return;
    if (task.isRecurring && !scope) {
      setPendingScope({ kind: "deleteTask" });
      return;
    }
    setDeleteError(null);
    try {
      if (scope === "series") {
        const seriesResult = await deleteTaskSeriesMutation({
          variables: { id: task.id },
        });
        if (seriesResult.data?.deleteTaskSeries?.success) {
          boardDirtyRef.current = true;
          setConfirmDeleteOpen(false);
          onOpenChange(false);
        } else {
          setDeleteError(
            seriesResult.data?.deleteTaskSeries?.message ||
              "Sarjan poisto epäonnistui.",
          );
        }
        return;
      }

      const result = await deleteTaskMutation({ variables: { id: task.id } });
      if (result.data?.deleteTask?.success) {
        // Ensure the Kanban board drops the now-deleted card on close.
        boardDirtyRef.current = true;
        setConfirmDeleteOpen(false);
        onOpenChange(false);
      } else {
        setDeleteError(result.data?.deleteTask?.message || "Tehtävän poisto epäonnistui.");
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Tehtävän poisto epäonnistui. Yritä uudelleen.";
      setDeleteError(msg);
      console.error("Failed to delete task:", error);
    }
  };

  const completedCount = subtasks.filter((s) => s.status === TaskStatus.COMPLETED).length;
  const totalCount = subtasks.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allDone = totalCount > 0 && completedCount === totalCount;

  // Show the loading skeleton only when there is no cached data yet.
  // During a background refetch (e.g. after a status change) we keep the
  // existing list visible so the progress bar can animate to its new value
  // rather than disappearing behind the skeleton.
  const isInitialLoad = subtasksLoading && subtasks.length === 0;

  // Natural-numeric sort: "1" < "2" < "10", also handles hierarchical
  // strings like "1.2" < "1.10". Falls back to title when external_order is
  // missing (non-Congrid subtasks or pre-backfill rows).
  const isCongridParent = task.taskType === TaskType.INSPECTION_MAIN_TASK;
  const naturalCmp = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

  const sortedSubtasks = [...subtasks].sort((a, b) => {
    if (isCongridParent) {
      const oa = a.externalOrder ?? "";
      const ob = b.externalOrder ?? "";
      if (oa && ob && oa !== ob) return naturalCmp(oa, ob);
      if (oa && !ob) return -1;
      if (!oa && ob) return 1;
    }
    return naturalCmp(a.title, b.title);
  });

  // Mirrors TaskSummaryCard's gate: no subtasks to derive a status from, not a
  // Congrid inspection task, and the viewer may write. Hidden while the
  // new-subtask form is open, since the task is about to gain children.
  const showCompletionToggle =
    totalCount === 0 &&
    !isCongridParent &&
    !task.externalInspectionPlatformInspectionId &&
    canWriteTask &&
    !showNewSubtask &&
    !isInitialLoad;

  const IconInProgress = TASK_STATUS_ICONS[TaskStatus.IN_PROGRESS];

  // Rendered in two places in the header — inline among the metadata chips on
  // md+, and on its own row under them on mobile — so it takes the class list
  // that decides which breakpoint shows it.
  const congridInspectionUrl = task.congridInspectionUrl;
  const renderCongridLink =
    task.taskType === TaskType.INSPECTION_MAIN_TASK && congridInspectionUrl
      ? (className: string) => (
          <a
            href={congridInspectionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border border-white/25 bg-white/15 px-2.5 py-1 text-xs font-semibold text-white hover:bg-white/25",
              className,
            )}
          >
            <CongridIcon className="h-3.5 w-3.5" />
            Avaa Congridissa ↗
          </a>
        )
      : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Mobile: full-screen. md+: centred card capped at max-w-3xl. */}
        <DialogContent className="w-screen h-dvh pb-0 max-w-full max-h-dvh rounded-none overflow-hidden flex flex-col border-0 shadow-xl [&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center [&>button]:justify-center [&>button]:text-white [&>button]:opacity-80 [&>button]:rounded-md [&>button:hover]:opacity-100 [&>button:hover]:bg-white/15 md:w-full md:h-[min(720px,90dvh)] md:max-w-3xl md:max-h-full md:rounded-lg">
          {/* Single brand-coloured header: title on top, then a metadata row
              (project / PP / work section) with the responsible person on the
              right — all on the primary card so it reads as one block.
              The title's right padding reserves room for the close X and the
              Muokkaa / Poista controls sharing its row in the top-right
              overlay — three 32px icon buttons on mobile, labelled buttons on
              md+. */}
          <DialogHeader className="bg-primary text-primary-foreground md:rounded-t-lg text-left space-y-3">
            <DialogTitle asChild>
              <h2 className="text-lg md:text-2xl font-semibold tracking-tight leading-tight text-primary-foreground break-words pr-32 md:pr-56">
                {task.isRecurring && (
                  <RecurrenceIcon
                    className="mr-2 inline h-4 w-4 align-baseline text-primary-foreground/70"
                    aria-label="Toistuva tehtävä"
                  />
                )}
                {task.section?.name
                  ? `${task.section.name}, ${task.title.toLowerCase()}`
                  : task.procurementPackage?.name
                    ? `${task.procurementPackage.name}, ${task.title.toLowerCase()}`
                    : task.title}
              </h2>
            </DialogTitle>

            {task.isOffSchedule && (
              <p className="text-xs text-primary-foreground/75">
                Sarjan toistuvuutta on muutettu. Tämä tehtävä säilytettiin
                alkuperäisellä päivällään
                {task.occurrenceDate
                  ? ` ${formatDateFi(task.occurrenceDate)}`
                  : ""}
                .
              </p>
            )}

            {/* Metadata on the left, Vastuu beside it on the right — at every
                width. On mobile the metadata stacks one item per line and
                Vastuu sits alongside that stack rather than under it. */}
            <div className="flex items-start justify-between gap-3 text-primary-foreground/85 md:items-center">
              <div className="flex min-w-0 flex-col gap-1.5 text-sm md:flex-row md:flex-wrap md:items-center md:gap-x-4">
                {(() => {
                  const pp = task.procurementPackage || task.section?.procurementPackage;
                  const ws = task.section;
                  const projectLabel = task.project?.name || null;
                  const ppLabel = pp ? [pp.code, pp.name].filter(Boolean).join(" ") : null;
                  const wsLabel = ws ? [ws.code, ws.name].filter(Boolean).join(" ") : null;
                  return (
                    <>
                      {projectLabel && (
                        <span className="flex min-w-0 items-center gap-1.5">
                          <ProjectIcon className="h-4 w-4 shrink-0 text-primary-foreground/60" />
                          <span className="truncate">{projectLabel}</span>
                        </span>
                      )}
                      {ppLabel && (
                        <span className="flex min-w-0 items-center gap-1.5">
                          <ProcurementPackageIcon className="h-4 w-4 shrink-0 text-primary-foreground/60" />
                          <span className="truncate">{ppLabel}</span>
                        </span>
                      )}
                      {wsLabel && (
                        <span className="flex min-w-0 items-center gap-1.5">
                          <WorkSectionIcon className="h-4 w-4 shrink-0 text-primary-foreground/60" />
                          <span className="truncate">{wsLabel}</span>
                        </span>
                      )}
                      {renderCongridLink?.("hidden md:inline-flex")}
                    </>
                  );
                })()}
              </div>

              {/* Responsible person, right-aligned on the same card. On mobile
                  the Congrid link stacks directly beneath it inside this
                  column, so it doesn't have to clear the taller metadata
                  column beside it. */}
              <div className="flex shrink-0 flex-col items-end gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="text-right leading-tight">
                    <div className="text-xs text-primary-foreground/70">Vastuu</div>
                    <div className="max-w-[7.5rem] truncate text-sm text-primary-foreground md:max-w-[12rem]">
                      {formatResponsible(task.responsibleUser) || "–"}
                    </div>
                  </div>
                  <Avatar className="h-9 w-9 border border-white/25">
                    <AvatarFallback className="bg-white/15 text-white text-xs font-semibold">
                      {effectiveInitials(task.responsibleUser) || (
                        <UserIcon className="h-4 w-4" />
                      )}
                    </AvatarFallback>
                  </Avatar>
                </div>
                {renderCongridLink && (
                  <div className="flex md:hidden items-center gap-2">
                    {renderCongridLink("")}
                  </div>
                )}
              </div>
            </div>

            <DialogDescription className="sr-only">
              {task.title}
            </DialogDescription>
          </DialogHeader>

          <DialogTabs
            defaultValue="subtasks"
          >
              <DialogTabsList columns={3}>
                <DialogTabsTrigger value="subtasks" className="gap-2">
                  {task.taskType === TaskType.INSPECTION_MAIN_TASK ? "Tarkastuslista" : "Alitehtävät"}
                  {totalCount > 0 && (
                    <span
                      className={cn(
                        "text-xs font-medium rounded-full px-2 py-0.5 tabular-nums transition-colors",
                        allDone
                          ? "bg-green-100 text-green-700"
                          : "bg-slate-100 text-slate-600",
                      )}
                    >
                      {completedCount}/{totalCount}
                    </span>
                  )}
                  </DialogTabsTrigger>
                <DialogTabsTrigger value="tiedot">Tiedot</DialogTabsTrigger>
                <DialogTabsTrigger value="comments">Kommentit</DialogTabsTrigger>
              </DialogTabsList>

              <DialogTabsContent
                value="subtasks"
                className="relative flex flex-col pt-4 data-[state=inactive]:hidden"
              >
                {subtaskError && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4 flex items-center justify-between shrink-0">
                    <span>{subtaskError}</span>
                    <button
                      onClick={() => setSubtaskError(null)}
                      className="text-red-500 hover:text-red-700 ml-3 shrink-0"
                    >
                      <CloseIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {totalCount > 0 && (
                  <div className="flex flex-row items-center pb-2 shrink-0 gap-2 border-b">
                    <span
                      className={cn(
                        "shrink-0 whitespace-nowrap text-xs font-medium tabular-nums transition-colors",
                        allDone
                          ? "text-green-700"
                          : "text-slate-600",
                      )}
                    >
                      {completedCount}/{totalCount}{allDone && task.taskType === TaskType.INSPECTION_MAIN_TASK && (
                        ", Kaikki tarkastukset tehty"
                      )}{allDone && task.taskType != TaskType.INSPECTION_MAIN_TASK && (
                        ", Kaikki alitehtävät valmiina"
                      )}
                    </span>
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-[width] duration-500 ease-out",
                          allDone ? "bg-green-500" : "bg-primary",
                        )}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>
                )}
                {isInitialLoad ? (
                  <div
                    className="space-y-2 motion-safe:animate-[fadeIn_0.2s_ease-out]"
                    role="status"
                    aria-label="Haetaan alitehtäviä"
                  >
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 py-2.5 px-3 rounded-md border border-border/60 bg-card/60"
                        style={{
                          animation:
                            "subtaskPulse 1.4s ease-in-out infinite",
                          animationDelay: `${i * 0.12}s`,
                        }}
                      >
                        <div className="mt-0.5 h-5 w-5 rounded-full bg-muted/60 overflow-hidden relative">
                          <div className="absolute inset-0 motion-safe:animate-[shimmer_1.6s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-background/80 to-transparent" />
                        </div>
                        <div className="flex-1 space-y-1.5 min-w-0">
                          <div
                            className="h-3 rounded bg-muted/60 overflow-hidden relative"
                            style={{ width: `${70 - i * 8}%` }}
                          >
                            <div className="absolute inset-0 motion-safe:animate-[shimmer_1.6s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-background/80 to-transparent" />
                          </div>
                          {i % 2 === 0 && (
                            <div
                              className="h-2.5 rounded bg-muted/40 overflow-hidden relative"
                              style={{ width: `${45 - i * 5}%` }}
                            >
                              <div className="absolute inset-0 motion-safe:animate-[shimmer_1.6s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-background/80 to-transparent" />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <span className="sr-only">Haetaan alitehtäviä…</span>
                  </div>
                ) : subtasks.length === 0 && !showNewSubtask ? (
                  <div className="text-center py-12 space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Ei alitehtäviä vielä.
                      {canAddSubtask && " Pilko tehtävä pienempiin paloihin."}
                    </p>
                    {canAddSubtask && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewSubtask(true)}
                        className="motion-safe:transition-transform motion-safe:hover:-translate-y-0.5"
                      >
                        <AddIcon className="h-4 w-4 mr-1" />
                        Lisää ensimmäinen alitehtävä
                      </Button>
                    )}
                  </div>
                ) : (
                  <ScrollArea className="flex-1">
                    <div className="space-y-3 pb-6 pr-4">
                      {sortedSubtasks.map((subtask, index) => (
                        editingSubtaskId === subtask.id ? (
                          <EditableSubtaskItem
                            key={subtask.id}
                            subtask={subtask}
                            onSave={(title, description) => handleEditSubtask(subtask.id, title, description)}
                            onCancel={() => setEditingSubtaskId(null)}
                            saving={subtaskSaving}
                          />
                        ) : (
                          <SubtaskItem
                            key={subtask.id}
                            subtask={subtask}
                            indexLabel={
                              task.taskType === TaskType.INSPECTION_MAIN_TASK
                                ? subtask.externalOrder
                                  ? `${subtask.externalOrder}.`
                                  : `${index + 1}.`
                                : undefined
                            }
                            onUpdateStatus={handleSubtaskStatusUpdate}
                            // Per-subtask, not per-parent: the backend answers
                            // viewerCanWrite for each row, so the controls
                            // follow that row rather than the parent's.
                            canManage={subtask.viewerCanWrite ?? false}
                            canEditStatus={subtask.viewerCanWrite ?? false}
                            onEdit={(s) => setEditingSubtaskId(s.id)}
                            onDelete={(s) => setSubtaskToDelete(s)}
                            parentCongridUrl={task.congridInspectionUrl ?? null}
                          />
                        )
                      ))}

                      {showNewSubtask && (
                        <NewSubtaskForm
                          onSave={handleCreateSubtask}
                          onCancel={() => setShowNewSubtask(false)}
                          saving={subtaskSaving}
                        />
                      )}

                      {canAddSubtask && !showNewSubtask && subtasks.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowNewSubtask(true)}
                          className="w-full justify-start text-muted-foreground hover:text-foreground mt-2 motion-safe:transition-colors"
                        >
                          <AddIcon className="h-4 w-4 mr-1" />
                          Lisää alitehtävä
                        </Button>
                      )}
                    </div>
                  </ScrollArea>
                )}
                {showCompletionToggle && (
                  <CompletionToggle
                    taskId={task.id}
                    status={task.status}
                    version={task.version}
                    onToggle={handleToggleCompleted}
                    onCompleted={fireConfetti}
                    size="dialog"
                    className="bottom-4 right-4 z-10"
                  />
                )}
              </DialogTabsContent>

              <DialogTabsContent
                value="tiedot"
                className="data-[state=inactive]:hidden"
              >
                <ScrollArea className="h-full">
                  <div className="space-y-8 py-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
                      {task.project?.name && (
                        <TaskInfoField
                          icon={<ProjectIcon className="h-4 w-4" />}
                          label="Hanke"
                        >
                          <p className="text-sm">{task.project.name}</p>
                        </TaskInfoField>
                      )}
                      {(() => {
                        const pp = task.procurementPackage || task.section?.procurementPackage;
                        if (!pp) return null;
                        const label = [pp.code, pp.name].filter(Boolean).join(" ");
                        return (
                          <TaskInfoField
                            icon={<ProcurementPackageIcon className="h-4 w-4" />}
                            label="Hankintapaketti"
                          >
                            <p className="text-sm">{label || "–"}</p>
                          </TaskInfoField>
                        );
                      })()}
                      {task.section && (
                        <TaskInfoField
                          icon={<WorkSectionIcon className="h-4 w-4" />}
                          label="Työvaihe"
                        >
                          <p className="text-sm">
                            {[task.section.code, task.section.name].filter(Boolean).join(" ") || "–"}
                          </p>
                        </TaskInfoField>
                      )}
                      {task.externalInspectionPlatformInspectionId && (
                        <TaskInfoField
                          icon={<CongridIcon className="h-4 w-4" />}
                          label="Congrid ID"
                        >
                          <p className="text-sm">
                            {task.externalInspectionPlatformInspectionId || "–"}
                          </p>
                        </TaskInfoField>
                      )}
                      {task.plot?.name && (
                        <TaskInfoField
                          icon={<PlotIcon className="h-4 w-4" />}
                          label="Tontti"
                        >
                          <p className="text-sm">{task.plot.name}</p>
                        </TaskInfoField>
                      )}
                      {task.location?.hierarchyPath && task.location.hierarchyPath.length > 0 && (
                        <TaskInfoField
                          icon={<SpaceIcon className="h-4 w-4" />}
                          label="Sijainti"
                        >
                          <p className="text-sm">{task.location.hierarchyPath.join(" > ")}</p>
                        </TaskInfoField>
                      )}
                      <TaskInfoField
                        icon={<UserIcon className="h-4 w-4" />}
                        label="Vastuuhenkilö"
                      >
                        <p className="text-sm">
                          {formatResponsible(task.responsibleUser) || "–"}
                        </p>
                      </TaskInfoField>
                      <TaskInfoField
                        icon={<DateIcon className="h-4 w-4" />}
                        label="Määräpäivä"
                      >
                        <p
                          className={cn(
                            "text-sm",
                            task.dueDate && task.status !== TaskStatus.COMPLETED
                              ? getDueDateInfo(task.dueDate)?.colorClass
                              : undefined,
                          )}
                        >
                          {task.dueDate ? formatDateFi(task.dueDate) : "–"}
                        </p>
                      </TaskInfoField>
                      {task.startedAt && (
                        <TaskInfoField
                          icon={<IconInProgress className={cn("h-4 w-4", TASK_STATUS_COLORS[TaskStatus.IN_PROGRESS].text)} />}
                          label="Aloitettu"
                        >
                          <p
                            className="text-sm"
                            title={new Date(task.startedAt).toLocaleString("fi-FI")}
                          >
                            {formatRelativeTimeFi(task.startedAt)}
                          </p>
                        </TaskInfoField>
                      )}
                      {task.completedAt && (
                        <TaskInfoField
                          icon={<SuccessIcon className={cn("h-4 w-4", TASK_STATUS_COLORS[TaskStatus.COMPLETED].text)} />}
                          label="Valmistunut"
                        >
                          <p
                            className="text-sm"
                            title={new Date(task.completedAt).toLocaleString("fi-FI")}
                          >
                            {formatRelativeTimeFi(task.completedAt)}
                          </p>
                        </TaskInfoField>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
                        <DocumentIcon className="h-4 w-4" />
                        Kuvaus
                      </div>
                      {/* Sitedrive work-package level shown plainly above the
                          description (inspection tasks). */}
                      {task.sitedriveMetadata?.sitedriveWorkPackage && (
                        <p className="text-sm">
                          {task.sitedriveMetadata.sitedriveWorkPackage}
                        </p>
                      )}
                      {task.description ? (
                        <p className="text-sm">{task.description}</p>
                      ) : (
                        !task.sitedriveMetadata?.sitedriveWorkPackage && (
                          <p className="text-sm italic text-muted-foreground">
                            Ei kuvausta
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </ScrollArea>
              </DialogTabsContent>

              <DialogTabsContent
                value="comments"
                className="flex flex-col py-4 data-[state=inactive]:hidden"
              >
                <ScrollArea className="flex-1">
                  {comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      Ei kommentteja
                    </p>
                  ) : (
                    <div className="space-y-4 pr-4 pb-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="flex gap-3 pb-3 border-b border-border/50 last:border-0">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-primary/10 text-primary text-xs">
                              {(comment.writer?.firstName?.charAt(0) ?? "") +
                                (comment.writer?.lastName?.charAt(0) ?? "")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">
                                {(comment.writer?.firstName ?? "") +
                                  " " +
                                  (comment.writer?.lastName ?? "")}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatRelativeTimeFi(comment.createdAt)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">
                              {comment.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                {/* Posting a comment is gated by Capability.COMMENT, which every
                    ProjectRoleType carries — so this is deliberately NOT tied to
                    viewerCanWrite. A Työnjohtaja may comment on a task they do
                    not own. Users with no role read the thread above only. */}
                {canComment && (
                  <div className="pt-4 mt-6">
                    {commentError && (
                      <p className="text-sm text-red-600 mb-3">{commentError}</p>
                    )}
                    <div className="flex gap-3">
                      <Textarea
                        placeholder="Kirjoita kommentti..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="min-h-[60px]"
                      />
                      <Button
                        onClick={handleAddComment}
                        disabled={!newComment.trim() || creatingComment}
                        className="self-end"
                      >
                        {creatingComment ? "Lähetetään..." : "Lähetä"}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogTabsContent>
          </DialogTabs>

          {/* Shares the close X's row in the top-right. Icon-only at 32px on
              mobile so all three buttons line up; labelled on md+. */}
          {canWriteTask && (
            <div className="absolute right-14 top-4 z-50 flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditMetadataOpen(true)}
                title="Muokkaa tietoja"
                className="h-8 w-8 justify-center p-0 text-white opacity-80 hover:opacity-100 hover:bg-white/15 rounded-md md:w-auto md:gap-2 md:p-2"
              >
                <EditIcon className="h-4 w-4" />
                <span className="sr-only md:not-sr-only">Muokkaa</span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDeleteOpen(true)}
                title="Poista tehtävä"
                className="h-8 w-8 justify-center p-0 text-white opacity-80 hover:opacity-100 hover:bg-red-500/25 rounded-md md:w-auto md:gap-2 md:p-2"
              >
                <DeleteIcon className="h-4 w-4" />
                <span className="sr-only md:not-sr-only">Poista</span>
              </Button>
            </div>
          )}
          <ConfettiBurst runId={confettiRun} className="z-50" />
        </DialogContent>
      </Dialog>

      <TaskMetadataEditDialog
        task={task}
        open={editMetadataOpen}
        onOpenChange={setEditMetadataOpen}
        onSaved={onTaskUpdated}
        subtasks={subtasks}
        subtasksLoading={subtasksLoading}
        onTopicsRewritten={() => {
          void refetchSubtasks();
          // The rewrite repoints the task at a new Congrid inspection, so the
          // cached detail — and the Congrid link built from it — is stale.
          void refetchTaskDetail().then((result) => {
            const fresh = result.data?.task;
            if (fresh) onTaskUpdated?.(fresh);
          });
        }}
      />

      {/* Delete subtask confirmation dialog */}
      <AlertDialog open={!!subtaskToDelete} onOpenChange={(open) => { if (!open) setSubtaskToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista alitehtävä</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa alitehtävän "{subtaskToDelete?.title}"? Tätä toimintoa ei voi perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Preventing the default close keeps the confirm open while the
                // scope prompt asks how far the deletion reaches.
                e.preventDefault();
                void handleDeleteSubtask();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete task confirmation dialog */}
      <AlertDialog
        open={confirmDeleteOpen}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmDeleteOpen(false);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Poista tehtävä</AlertDialogTitle>
            <AlertDialogDescription>
              Haluatko varmasti poistaa tehtävän "{task.title}"? Tätä toimintoa ei voi perua.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel>Peruuta</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteTask();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Poista
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SeriesScopeDialog
        open={pendingScope !== null}
        onOpenChange={(open) => {
          if (!open) setPendingScope(null);
        }}
        mode={
          pendingScope?.kind === "deleteTask" ||
          pendingScope?.kind === "deleteSubtask"
            ? "delete"
            : "edit"
        }
        busy={subtaskSaving}
        onChoose={(scope) => {
          const pending = pendingScope;
          setPendingScope(null);
          if (!pending) return;
          if (pending.kind === "deleteTask") {
            void handleDeleteTask(scope);
          } else if (pending.kind === "addSubtask") {
            void handleCreateSubtask(pending.title, pending.description, scope);
          } else if (pending.kind === "editSubtask") {
            void handleEditSubtask(
              pending.subtaskId,
              pending.title,
              pending.description,
              scope,
            );
          } else {
            void handleDeleteSubtask(scope);
          }
        }}
      />
    </>
  );
}

function TaskInfoField({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
        {icon}
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
